import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { PDFCheckBox, PDFDocument } from "pdf-lib";
import { handleDynamicFontResize } from "../../../../../uploaderComponents/helpers/processFont";
import { handleMouseMove } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseMove";
import { handleMouseUp } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseUp";
import { handleKeyDown } from "../../../../../uploaderComponents/helpers/keyEvents.js/keyDown";
import { sortInitials } from "../../../../../permitForms/permitFormHelpers";
import {
  arrayBufferToBase64DataUrl,
  base64ToUint8Array,
  hasPDFHeader,
} from "../utils/pdfDataUtils";
import { useUndoRedo } from "./useUndoRedo";
import { getEffectivePageRotation } from "../utils/documentOrientation";
import axios from "axios";

export const MIN_SCALE = 0.5;
export const MAX_SCALE = 4.0;
export const SCALE_STEP = 0.1;
export const BASE_SCALE = 2.0;

export const clampScale = (value) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(Number(value).toFixed(1))));

const normalizeEditorFields = (fields = []) =>
  (fields || []).map((field) => {
    if (field?.type === "PDFCheckBox") {
      return {
        ...field,
        value: false,
      };
    }
    if (field?.type !== "PDFTextField") {
      return field;
    }

    return {
      ...field,
      autoFontSize: true,
    };
  });

const createPersistedEditorSnapshot = ({
  formFields = [],
  pdfTextEdits = [],
  title = "",
} = {}) =>
  JSON.stringify({
    formFields,
    pdfTextEdits,
    title,
  });

const clearPdfCheckboxes = async (base64File) => {
  if (!base64File) return base64File;

  try {
    const pdfDocument = await PDFDocument.load(base64ToUint8Array(base64File));
    const checkboxes = pdfDocument
      .getForm()
      .getFields()
      .filter((field) => field instanceof PDFCheckBox);

    if (checkboxes.length === 0) return base64File;

    checkboxes.forEach((checkbox) => checkbox.uncheck());

    return arrayBufferToBase64DataUrl(await pdfDocument.save());
  } catch (error) {
    console.error("Error clearing PDF checkboxes:", error);
    return base64File;
  }
};

const getFieldPage = (field) => {
  const page = Number(field?.page_num ?? field?.page);
  return Number.isFinite(page) ? page : 1;
};

const getFieldMetric = (field, key, rectKey) => {
  const value = Number(field?.[key] ?? field?.rect?.[rectKey] ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const splitSectionFieldsByGap = (fields = []) => {
  const sorted = [...fields].sort(
    (a, b) => getFieldMetric(a, "y", "Y") - getFieldMetric(b, "y", "Y"),
  );

  const groups = [];
  for (const field of sorted) {
    const prev = groups[groups.length - 1]?.at(-1);
    const gap = prev
      ? getFieldMetric(field, "y", "Y") -
        (getFieldMetric(prev, "y", "Y") + getFieldMetric(prev, "height", "H"))
      : Infinity;

    if (!prev || gap > 15) groups.push([field]);
    else groups[groups.length - 1].push(field);
  }
  return groups;
};

const hydrateSectionMarkers = (fields = [], pageNumber, pageWidth) => {
  const fieldsOnPage = [];
  const existingMarkerBlockIds = new Set();

  for (const field of fields) {
    if (getFieldPage(field) !== pageNumber) continue;
    fieldsOnPage.push(field);
    if (field?.block_id && field?.name?.startsWith("block__")) {
      existingMarkerBlockIds.add(field.block_id);
    }
  }

  const sectionFieldsByBlockId = new Map();
  for (const field of fieldsOnPage) {
    if (
      !field?.block_id ||
      field?.name?.startsWith("block__") ||
      existingMarkerBlockIds.has(field.block_id)
    ) {
      continue;
    }
    const group = sectionFieldsByBlockId.get(field.block_id);
    if (group) group.push(field);
    else sectionFieldsByBlockId.set(field.block_id, [field]);
  }

  if (sectionFieldsByBlockId.size === 0) return fields;

  const splitBlockIdByField = new Map();
  const markerSeed = Date.now().toString(36);
  const sectionMarkers = [];

  sectionFieldsByBlockId.forEach((sectionFields, blockId) => {
    const splitGroups = splitSectionFieldsByGap(sectionFields);

    splitGroups.forEach((groupFields, groupIndex) => {
      const splitBlockId =
        splitGroups.length === 1 ? blockId : `${blockId}__sub_${groupIndex}`;

      let top = Infinity;
      let bottom = -Infinity;
      for (const field of groupFields) {
        const y = getFieldMetric(field, "y", "Y");
        const fieldBottom = y + getFieldMetric(field, "height", "H");
        if (y < top) top = y;
        if (fieldBottom > bottom) bottom = fieldBottom;
        splitBlockIdByField.set(field, splitBlockId);
      }

      sectionMarkers.push({
        block_id: splitBlockId,
        block_label: groupFields[0]?.block_label,
        name: `block__${markerSeed}_${sectionMarkers.length}`,
        type: "PDFRectangle",
        value: "",
        x: 20,
        y: top - 3,
        width: Math.max(Number(pageWidth) - 50, 0),
        height: bottom - top + 4,
        page: pageNumber,
        fontSize: bottom - top + 4,
        fontSizes: [],
        autoFontSize: true,
        hidden: groupFields.every((field) => field?.hidden),
      });
    });
  });

  const normalizedFields = fields.map((field) => {
    const splitBlockId = splitBlockIdByField.get(field);
    return splitBlockId && splitBlockId !== field.block_id
      ? { ...field, block_id: splitBlockId }
      : field;
  });

  return [...normalizedFields, ...sectionMarkers];
};

export const usePdfEditorState = ({
  formData,
  isOpen,
  onClose,
  onSave,
  onNotify,
  editableTitle,
  displayRotation,
}) => {
  const [currentProcessingFile, setCurrentProcessingFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(2.0);
  const [pdfSize, setPdfSize] = useState(800);
  const [actualWidth, setActualWidth] = useState(612);
  const [actualHeight, setActualHeight] = useState(792);
  const [pdfInput, setPdfInput] = useState(null);
  const [base64File, setBase64File] = useState(null);

  const [resizing, setResizing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [rect, setRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [selectionRect, setSelectionRect] = useState(null);
  const [freeSelectStartPoint, setFreeSelectStartPoint] = useState(null);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [initialMousePosition, setInitialMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [initialInputSize, setInitialInputSize] = useState({
    width: 0,
    height: 0,
  });
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
  const [currCursor, setCurrCursor] = useState("default");
  const [resizeDirection, setResizeDirection] = useState("");

  const [selectToolActive, setSelectToolActive] = useState(true);
  const [typingToolActive, setTypingToolActive] = useState(false);
  const [expandToolBox, setExpandToolBox] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [reverseDeleted, setReverseDeleted] = useState([]);
  const [focusedField, setFocusedField] = useState(null);
  const [highlightFields, setHighlightFields] = useState(true);
  const [textSelectionMode, setTextSelectionMode] = useState(true);
  const [textOverlayDisplayMode, setTextOverlayDisplayMode] = useState("label");
  const [paisTableSelectActive, setPaisTableSelectActive] = useState(false);
  const [unassignedFieldNames, setUnassignedFieldNames] = useState([]);
  const [activeEditorTool, setActiveEditorTool] = useState("edit");

  const pdfContainerRef = useRef(null);
  const pdfScrollContainerRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const extractedOnceRef = useRef(false);
  const initializedFormKeyRef = useRef(null);
  const pendingHydrationSnapshotRef = useRef(null);
  const [initialPersistedSnapshot, setInitialPersistedSnapshot] =
    useState(null);
  const inputRef = useRef(null);
  const childInputRef = useRef(null);

  const effectiveEditorTitle =
    editableTitle ||
    formData?.guessedFormDetails?.label_guess ||
    formData?.formName ||
    formData?.fileName ||
    "Untitled Form";
  const persistedFormFields =
    currentProcessingFile?.document?.tempFields?.formFields;

  const persistedPdfTextEdits =
    currentProcessingFile?.document?.tempFields?.pdfTextEdits;

  const persistedEditorSnapshot = useMemo(
    () =>
      createPersistedEditorSnapshot({
        formFields: persistedFormFields || [],
        pdfTextEdits: persistedPdfTextEdits || [],
        title: effectiveEditorTitle,
      }),
    [persistedFormFields, persistedPdfTextEdits, effectiveEditorTitle],
  );
  const pendingHydrationSnapshot = pendingHydrationSnapshotRef.current;
  const isApplyingInitialHydration = Boolean(
    pendingHydrationSnapshot &&
      initialPersistedSnapshot === pendingHydrationSnapshot.before &&
      persistedEditorSnapshot === pendingHydrationSnapshot.after,
  );
  const hasUnsavedChanges =
    initialPersistedSnapshot !== null &&
    initialPersistedSnapshot !== persistedEditorSnapshot &&
    !isApplyingInitialHydration;

  useEffect(() => {
    const pendingSnapshot = pendingHydrationSnapshotRef.current;
    if (!pendingSnapshot) return;

    if (initialPersistedSnapshot !== pendingSnapshot.before) {
      pendingHydrationSnapshotRef.current = null;
      return;
    }

    if (persistedEditorSnapshot === pendingSnapshot.after) {
      pendingHydrationSnapshotRef.current = null;
      setInitialPersistedSnapshot((currentSnapshot) =>
        currentSnapshot === pendingSnapshot.before
          ? pendingSnapshot.after
          : currentSnapshot,
      );
      return;
    }

    if (persistedEditorSnapshot !== pendingSnapshot.before) {
      pendingHydrationSnapshotRef.current = null;
    }
  }, [initialPersistedSnapshot, persistedEditorSnapshot]);

  const { pushHistory, undo, redo, canRedo, canUndo } = useUndoRedo(
    currentProcessingFile,
    setCurrentProcessingFile,
  );

  const fixedInputs = useMemo(
    () => [
      "notary_seal_a",
      "notary_sign_a",
      "notary_seal_b",
      "notary_sign_b",
      "sign_a",
      "sign_b",
      "sign_c",
    ],
    [],
  );

  const handleFieldChange = useCallback((e, field) => {
    const newValue = e.target.value;
    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: prevFile.document.tempFields.formFields.map((f) => {
            if (f.name !== field.name) return f;
            if (!f.autoFontSize) return { ...f, value: newValue };

            const { newFontSize, fontSizes } = handleDynamicFontResize(
              field.width,
              field.height,
              f.fontSize,
              newValue,
              f.fontSizes || [],
              f.value,
            );

            return { ...f, value: newValue, fontSize: newFontSize, fontSizes };
          }),
        },
      },
    }));
  }, []);

  const validateAndProcessFile = useCallback(async (file) => {
    if (!file || file.type !== "application/pdf") return;

    const reader = new FileReader();
    reader.onload = async function () {
      const base64String = reader.result;
      const uint8Data = base64ToUint8Array(base64String);
      if (!hasPDFHeader(uint8Data)) return;

      try {
        await pdfjsLib.getDocument({ data: uint8Data }).promise;
        const sanitizedBase64File = await clearPdfCheckboxes(base64String);
        setBase64File(sanitizedBase64File);
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              currentDocument: {
                ...prevFile.document.tempFields.currentDocument,
                base64File: sanitizedBase64File,
                files: "",
              },
            },
          },
        }));
      } catch (e) {
        console.error("Error loading PDF:", e);
      }
    };

    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    if (!formData || !isOpen) return;

    const formIdentityKey =
      formData?.formId ||
      formData?.form_id ||
      formData?._id ||
      formData?.id ||
      formData?.fileName ||
      "default_form";

    const hasFormIdNow = Boolean(formData?.formId);
    const hadFormIdBefore =
      initializedFormKeyRef.current &&
      initializedFormKeyRef.current !== "default_form" &&
      !String(initializedFormKeyRef.current).includes(".");

    if (
      initializedFormKeyRef.current === formIdentityKey &&
      !(hasFormIdNow && !hadFormIdBefore)
    ) {
      return;
    }

    initializedFormKeyRef.current = formIdentityKey;

    if (formData?.skipPdfInit) {
      setBase64File(null);
      setPdfInput(null);
      return;
    }

    const initialFormFields = sortInitials(
      normalizeEditorFields(formData.formFields || []),
    ).map((f, i) => ({ ...f, order: f?.order ?? i + 1 }));
    const initialPdfTextEdits = formData.pdfTextEdits || [];
    const initialTitle =
      formData?.guessedFormDetails?.label_guess ||
      formData?.formName ||
      formData?.fileName ||
      "Untitled Form";

    pendingHydrationSnapshotRef.current = null;
    setInitialPersistedSnapshot(
      createPersistedEditorSnapshot({
        formFields: initialFormFields,
        pdfTextEdits: initialPdfTextEdits,
        title: initialTitle,
      }),
    );

    setCurrentProcessingFile({
      document: {
        tempFields: {
          currentDocument: {
            base64File: formData.base64File || null,
            pdfSize,
            actualWidth,
            actualHeight,
            pageXMin: 0,
            pageYMin: 0,
            viewport: null,
            scaleFactor: pdfSize / (actualWidth || 1),
          },
          formFields: initialFormFields,
          pdfTextItemsByPage: formData.pdfTextItemsByPage || {},
          pdfTextEdits: initialPdfTextEdits,
          selectedPdfTextItem: null,
          selectedExtraPdfTextItems: [],
          selectedFormField: null,
          selectedExtraFields: [],
          formFieldTools: {
            textField: { active: false, name: "textField" },
            checkbox: { active: false, name: "checkbox" },
            insertText: { active: false, name: "insertText" },
            rectangle: { active: false, name: "rectangle" },
            line: { active: false, name: "line" },
            radio: { active: false, name: "radio" },
            date: { active: false, name: "date" },
            notarySeal_a: {
              active: false,
              name: "notary_seal_a",
              disabled: false,
            },
            notarySignature_a: {
              active: false,
              name: "notary_sign_a",
              disabled: false,
            },
            notarySeal_b: {
              active: false,
              name: "notary_seal_b",
              disabled: false,
            },
            notarySignature_b: {
              active: false,
              name: "notary_sign_b",
              disabled: false,
            },
            signatureA: { active: false, name: "sign_a", disabled: false },
            signatureB: { active: false, name: "sign_b", disabled: false },
            signatureC: { active: false, name: "sign_c", disabled: false },
          },
        },
      },
    });

    (async () => {
      if (formData.pdfFile) {
        await validateAndProcessFile(formData.pdfFile);
      } else if (formData.base64File) {
        const sanitizedBase64File = await clearPdfCheckboxes(
          formData.base64File,
        );
        setBase64File(sanitizedBase64File);
        setPdfInput(sanitizedBase64File);
        setCurrentProcessingFile((prev) => ({
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              currentDocument: {
                ...prev.document.tempFields.currentDocument,
                base64File: sanitizedBase64File,
              },
            },
          },
        }));
      } else if (formData.pdfUrl) {
        const res = await fetch(formData.pdfUrl, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
        const b64 = arrayBufferToBase64DataUrl(await res.arrayBuffer());
        const sanitizedBase64File = await clearPdfCheckboxes(b64);
        setBase64File(sanitizedBase64File);
        setPdfInput(sanitizedBase64File);
        setCurrentProcessingFile((prev) => ({
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              selectedPdfTextItem: null,
              currentDocument: {
                ...prev.document.tempFields.currentDocument,
                base64File: sanitizedBase64File,
              },
            },
          },
        }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isOpen, validateAndProcessFile]);

  useEffect(() => {
    if (!currentProcessingFile) return;
    const currentFields =
      currentProcessingFile?.document?.tempFields?.formFields || [];
    if (!unassignedFieldNames.length) return;

    const stillUnassigned = currentFields
      .filter((f) => !f?.block_id || !f?.field_id)
      .map((f) => f?.name)
      .filter(Boolean);

    if (stillUnassigned.length !== unassignedFieldNames.length) {
      setUnassignedFieldNames(stillUnassigned);
    }
  }, [currentProcessingFile, unassignedFieldNames]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (
        !currentProcessingFile?.document?.tempFields?.currentDocument
          ?.base64File
      )
        return;
      if (!e.ctrlKey) return;
      e.preventDefault();
      const currentScale =
        currentProcessingFile?.document?.tempFields?.currentDocument
          ?.scaleFactor || scale;
      const newScale = clampScale(
        e.deltaY < 0 ? currentScale + SCALE_STEP : currentScale - SCALE_STEP,
      );
      setScale(newScale);
      setPdfSize(newScale * actualWidth);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            currentDocument: {
              ...prevFile.document.tempFields.currentDocument,
              pdfSize: newScale * actualWidth,
              scaleFactor: newScale,
            },
          },
        },
      }));
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => document.removeEventListener("wheel", handleWheel);
  }, [currentProcessingFile, actualWidth, scale]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    currentProcessingFile,
    typingToolActive,
    selectToolActive,
    focusedField,
    currCursor,
    expandToolBox,
  ]);

  useEffect(() => {
    const handleKeyDownWrapper = (e) => {
      handleKeyDown(
        e,
        currentProcessingFile,
        setCurrentProcessingFile,
        typingToolActive,
        setReverseDeleted,
        setCurrCursor,
        selectToolActive,
        focusedField,
        setTypingToolActive,
        setSelectToolActive,
        currCursor,
        setFocusedField,
        setHighlightFields,
        expandToolBox,
        setExpandToolBox,
        activeEditorTool,
        setActiveEditorTool,
        pushHistory,
      );
    };

    document.addEventListener("keydown", handleKeyDownWrapper);

    return () => {
      document.removeEventListener("keydown", handleKeyDownWrapper);
    };
  }, [
    currentProcessingFile,
    typingToolActive,
    selectToolActive,
    focusedField,
    currCursor,
    expandToolBox,
    activeEditorTool,
    pushHistory,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resizing) return;
      const isInsidePdfViewer = pdfScrollContainerRef.current?.contains(
        event.target,
      );
      const clickedInsideInput = Boolean(event.target?.closest?.("input"));
      if (isInsidePdfViewer && !clickedInsideInput) {
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              selectedFormField: null,
              selectedExtraFields: [],
              selectedPdfTextItem: null,
              selectedExtraPdfTextItems: [],
            },
          },
        }));
      }
    };

    document.addEventListener("dblclick", handleClickOutside);
    return () => document.removeEventListener("dblclick", handleClickOutside);
  }, [resizing]);

  useEffect(() => {
    setCurrentProcessingFile((prev) => {
      if (!prev) return prev;
      const aw =
        prev?.document?.tempFields?.currentDocument?.actualWidth ||
        actualWidth ||
        1;
      const newScale =
        (prev?.document?.tempFields?.currentDocument?.pdfSize || pdfSize) / aw;
      return {
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              scaleFactor: newScale,
            },
          },
        },
      };
    });
  }, [pdfSize, actualWidth]);

  useEffect(() => {
    if (!isOpen) {
      extractedOnceRef.current = false;
      initializedFormKeyRef.current = null;
      pendingHydrationSnapshotRef.current = null;
      setInitialPersistedSnapshot(null);
    }
  }, [isOpen]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => setNumPages(numPages),
    [],
  );

  const onPageLoadSuccess = useCallback(
    (page) => {
      const view = Array.isArray(page?.view) ? page.view : [0, 0, 612, 792];
      const [xMin = 0, yMin = 0, xMax = 612, yMax = 792] = view;
      const width = page.originalWidth ? xMax - xMin : 612;
      const height = page.originalHeight ? yMax - yMin : 792;
      const effectiveRotation = getEffectivePageRotation(
        page?.rotate,
        displayRotation,
      );
      const viewport = page?.getViewport
        ? page.getViewport({ scale, rotation: effectiveRotation })
        : null;
      setActualWidth(width);
      setActualHeight(height);
      setPdfSize(scale * width);
      const tempFields = currentProcessingFile?.document?.tempFields;
      const currentFormFields = tempFields?.formFields || [];
      const hydratedFormFields = hydrateSectionMarkers(
        currentFormFields,
        pageNumber,
        width,
      );
      const snapshotBeforeHydration = createPersistedEditorSnapshot({
        formFields: currentFormFields,
        pdfTextEdits: tempFields?.pdfTextEdits || [],
        title: effectiveEditorTitle,
      });

      if (
        tempFields &&
        hydratedFormFields !== currentFormFields &&
        initialPersistedSnapshot === snapshotBeforeHydration
      ) {
        pendingHydrationSnapshotRef.current = {
          before: snapshotBeforeHydration,
          after: createPersistedEditorSnapshot({
            formFields: hydratedFormFields,
            pdfTextEdits: tempFields.pdfTextEdits || [],
            title: effectiveEditorTitle,
          }),
        };
      }

      setCurrentProcessingFile((prev) => {
        const prevTempFields = prev?.document?.tempFields;
        if (!prevTempFields) return prev;
        const formFieldsStillCurrent =
          prevTempFields.formFields === currentFormFields;

        return {
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prevTempFields,
              currentDocument: {
                ...prevTempFields.currentDocument,
                actualWidth: width,
                actualHeight: height,
                pageXMin: xMin,
                pageYMin: yMin,
                viewport: viewport
                  ? {
                      width: viewport.width,
                      height: viewport.height,
                      rotation: viewport.rotation,
                      transform: Array.isArray(viewport.transform)
                        ? [...viewport.transform]
                        : null,
                    }
                  : null,
                pdfSize: scale * width,
                scaleFactor: scale,
                pageNumber,
              },
              formFields: formFieldsStillCurrent
                ? hydratedFormFields
                : prevTempFields.formFields,
            },
          },
        };
      });
    },
    [
      currentProcessingFile,
      displayRotation,
      effectiveEditorTitle,
      initialPersistedSnapshot,
      scale,
      pageNumber,
    ],
  );

  const handleScaleChange = useCallback(
    (_, rawValue) => {
      const newValue = clampScale(rawValue);
      setScale(newValue);
      setPdfSize(newValue * actualWidth);
      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              pdfSize: newValue * actualWidth,
              scaleFactor: newValue,
            },
          },
        },
      }));
    },
    [actualWidth],
  );

  const handlePageChange = useCallback((_, value) => {
    setPageNumber(value);
    setIsSelecting(false);
    setSelectionRect(null);
    setFreeSelectStartPoint(null);
    setCurrentProcessingFile((prev) =>
      prev
        ? {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...prev.document.tempFields,
                selectedFormField: null,
                selectedExtraFields: [],
                selectedPdfTextItem: null,
                selectedExtraPdfTextItems: [],
                currentDocument: {
                  ...prev.document.tempFields.currentDocument,
                  pageNumber: value,
                },
              },
            },
          }
        : prev,
    );
  }, []);

  useEffect(() => {
    if (!pdfScrollContainerRef.current) return;
    pdfScrollContainerRef.current.scrollTop = 0;
  }, [pageNumber]);

  const handleToolToggle = useCallback((_, newTool) => {
    if (newTool === null) return;
    if (newTool === "hand") {
      setCurrCursor("default");
      setSelectToolActive(true);
      setTypingToolActive(false);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: null,
            selectedExtraFields: [],
            formFieldTools: Object.keys(
              prevFile.document.tempFields.formFieldTools,
            ).reduce(
              (acc, key) => ({
                ...acc,
                [key]: {
                  ...prevFile.document.tempFields.formFieldTools[key],
                  active: false,
                  values: [],
                },
              }),
              {},
            ),
          },
        },
      }));
    } else {
      setCurrCursor("default");
      setSelectToolActive(false);
      setTypingToolActive(true);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: null,
            selectedExtraFields: [],
          },
        },
      }));
    }
  }, []);

  const handleTogglePaisTableSelect = useCallback(() => {
    setPaisTableSelectActive((prev) => {
      const next = !prev;
      setCurrCursor("default");
      // Reuses the existing freeSelect drag pipeline in PDFCanvas.jsx,
      // same as the "hand"/edit tools do.
      if (next) {
        // PAIS Selection Mode
        setSelectToolActive(true);
        setTypingToolActive(false);
        setActiveEditorTool(null);
      } else {
        // Back to Edit Mode
        setSelectToolActive(true);
        setTypingToolActive(false);
        setCurrCursor("default");
        setActiveEditorTool("edit");
      }
      setCurrentProcessingFile((prevFile) => {
        if (!prevFile) return prevFile;
        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              selectedFormField: null,
              selectedExtraFields: [],
              selectedPdfTextItem: null,
              selectedExtraPdfTextItems: [],
            },
          },
        };
      });
      return next;
    });
  }, []);

  const handleEditorToolSelection = useCallback(
    (toolKey) => {
      setActiveEditorTool((prevActiveTool) => {
        const nextTool = prevActiveTool === toolKey ? null : toolKey;
        if (
          toolKey === "rectangle" &&
          prevActiveTool === "edit" &&
          currentProcessingFile.document.tempFields?.selectedFormField?.type ===
            "PDFCheckBox"
        ) {
          pushHistory?.();
          const selectedField =
            currentProcessingFile.document.tempFields?.selectedFormField;

          const updatedFormFields = (
            currentProcessingFile.document.tempFields?.formFields || []
          ).map((field) => {
            const isTargetField = selectedField?.field_instance_id
              ? field?.field_instance_id === selectedField?.field_instance_id
              : field?.name === selectedField?.name;

            if (!isTargetField) return field;

            return {
              ...field,
              outline: true,
            };
          });

          const editedSelectedFormField = {
            ...selectedField,
            outline: true,
          };

          setCurrentProcessingFile((prevFile) => ({
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevFile.document.tempFields,
                formFields: updatedFormFields,
                selectedFormField: editedSelectedFormField,
              },
            },
          }));
          return prevActiveTool;
        }

        setPaisTableSelectActive(false);

        if (toolKey === "edit") {
          setCurrCursor("default");
          setSelectToolActive(Boolean(nextTool));
          setTypingToolActive(false);
          setCurrentProcessingFile((prevFile) => {
            if (!prevFile) return prevFile;
            return {
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  selectedFormField: null,
                  selectedExtraFields: [],
                  selectedPdfTextItem: null,
                  formFieldTools: Object.keys(
                    prevFile.document.tempFields.formFieldTools,
                  ).reduce(
                    (acc, key) => ({
                      ...acc,
                      [key]: {
                        ...prevFile.document.tempFields.formFieldTools[key],
                        active: false,
                      },
                    }),
                    {},
                  ),
                },
              },
            };
          });
        } else if (toolKey === "hand") {
          setCurrCursor(nextTool ? "grab" : "default");
          setSelectToolActive(false);
          setTypingToolActive(false);
          setCurrentProcessingFile((prevFile) => {
            if (!prevFile) return prevFile;
            return {
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  selectedFormField: null,
                  selectedExtraFields: [],
                  selectedPdfTextItem: null,
                  selectedExtraPdfTextItems: [],
                  formFieldTools: Object.keys(
                    prevFile.document.tempFields.formFieldTools,
                  ).reduce(
                    (acc, key) => ({
                      ...acc,
                      [key]: {
                        ...prevFile.document.tempFields.formFieldTools[key],
                        active: false,
                      },
                    }),
                    {},
                  ),
                },
              },
            };
          });
        } else {
          const toolMap = {
            checkbox: "checkbox",
            textField: "textField",
            insertText: "insertText",
            rectangle: "rectangle",
            line: "line",
          };
          const mappedTool = toolMap[toolKey];

          if (nextTool) {
            if (toolKey === "insertText") {
              setCurrCursor("text");
            } else {
              setCurrCursor("crosshair");
            }
          } else {
            setCurrCursor("default");
          }

          setSelectToolActive(false);
          setTypingToolActive(false);
          setCurrentProcessingFile((prevFile) => {
            if (!prevFile) return prevFile;
            return {
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  selectedFormField: null,
                  selectedExtraFields: [],
                  selectedPdfTextItem: null,
                  selectedExtraPdfTextItems: [],
                  formFieldTools: Object.keys(
                    prevFile.document.tempFields.formFieldTools,
                  ).reduce(
                    (acc, key) => ({
                      ...acc,
                      [key]: {
                        ...prevFile.document.tempFields.formFieldTools[key],
                        active: Boolean(
                          mappedTool && nextTool && key === mappedTool,
                        ),
                      },
                    }),
                    {},
                  ),
                },
              },
            };
          });
        }

        return nextTool;
      });
    },
    [currentProcessingFile, pushHistory],
  );

  const handleSave = useCallback(
    (isClose = false, saveOptions = {}) => {
      if (!onSave || !currentProcessingFile) return;

      const fields =
        currentProcessingFile?.document?.tempFields?.formFields || [];
      const unassigned = fields.filter((f) => !f?.block_id || !f?.field_id);
      const unassignedNames = unassigned
        .map((f, idx) => f?.name || `field_${idx + 1}`)
        .filter(Boolean);
      setUnassignedFieldNames(unassignedNames);
      const saveResult = onSave(
        {
          ...formData,
          formFields: fields,
          pdfTextEdits:
            currentProcessingFile?.document?.tempFields?.pdfTextEdits || [],
        },
        {
          title: editableTitle,
          isClose,
          ...saveOptions,
        },
      );

      return Promise.resolve(saveResult).then(async (wasSaved) => {
        if (wasSaved === false) return false;

        let savedFields = fields;

        const formId =
          formData?.form_id ||
          formData?.formId ||
          formData?.form?.form_id ||
          "";

        const closesEditor = saveOptions?.isClose ?? isClose;
        const hasNewWidgets = fields.some((field) => !field?.field_instance_id);

        if (!closesEditor && formId && hasNewWidgets) {
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-instances/get-form-data`,
              { formId },
              { withCredentials: true },
            );

            const persistedFields = response?.data?.data?.formFields;

            if (!response?.data?.success || !Array.isArray(persistedFields)) {
              throw new Error("Invalid saved-field response.");
            }

            const idsBySubmittedField = new Map();

            for (const field of fields) {
              if (field?.field_instance_id) continue;

              // Use the existing widget name only to reconcile its server ID.
              // Prefill targeting continues to use field_instance_id.
              const submittedMatches = fields.filter(
                (candidate) => candidate?.name === field?.name,
              );

              const persistedMatches = persistedFields.filter(
                (candidate) =>
                  candidate?.name === field?.name &&
                  (candidate.form_id == null || candidate.form_id === formId),
              );

              if (
                !field?.name ||
                submittedMatches.length !== 1 ||
                persistedMatches.length !== 1 ||
                typeof persistedMatches[0].field_instance_id !== "string" ||
                !persistedMatches[0].field_instance_id.trim()
              ) {
                throw new Error(
                  "Could not uniquely identify a newly saved widget.",
                );
              }

              idsBySubmittedField.set(
                field,
                persistedMatches[0].field_instance_id,
              );
            }

            const applySavedIdentity = (field) => {
              const instanceId = idsBySubmittedField.get(field);

              return instanceId
                ? {
                    ...field,
                    form_id: formId,
                    field_instance_id: instanceId,
                  }
                : field;
            };

            savedFields = fields.map(applySavedIdentity);

            setCurrentProcessingFile((previous) => {
              const tempFields = previous?.document?.tempFields;
              if (!tempFields) return previous;

              // Only reconcile submitted widget objects still in this editor.
              // Preserve changes made while the request was running.
              const nextFields = tempFields.formFields.map(applySavedIdentity);

              if (
                nextFields.every(
                  (field, index) => field === tempFields.formFields[index],
                )
              ) {
                return previous;
              }

              const refreshSelection = (selected) => {
                if (!selected || selected.field_instance_id) return selected;

                const matches = nextFields.filter(
                  (field) => field.name === selected.name,
                );

                return matches.length === 1 ? matches[0] : selected;
              };

              return {
                ...previous,
                document: {
                  ...previous.document,
                  tempFields: {
                    ...tempFields,
                    formFields: nextFields,
                    selectedFormField: refreshSelection(
                      tempFields.selectedFormField,
                    ),
                    selectedExtraFields: (
                      tempFields.selectedExtraFields || []
                    ).map(refreshSelection),
                  },
                },
              };
            });
          } catch (error) {
            console.error("Failed to synchronize saved widget IDs:", error);

            onNotify?.(
              "warning",
              "The form was saved, but widget IDs could not be refreshed. Save again to retry.",
            );
          }
        }

        pendingHydrationSnapshotRef.current = null;

        setInitialPersistedSnapshot(
          createPersistedEditorSnapshot({
            formFields: savedFields,
            pdfTextEdits:
              currentProcessingFile?.document?.tempFields?.pdfTextEdits || [],
            title: saveOptions?.title ?? effectiveEditorTitle,
          }),
        );

        return wasSaved;
      });
    },
    [
      onSave,
      onNotify,
      currentProcessingFile,
      formData,
      editableTitle,
      effectiveEditorTitle,
    ],
  );

  useEffect(() => {
    const handleSaveShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave(false);
      }
    };

    document.addEventListener("keydown", handleSaveShortcut);
    return () => document.removeEventListener("keydown", handleSaveShortcut);
  }, [handleSave]);

  const handleClose = useCallback(() => {
    setPageNumber(1);
    onClose?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onClose]);

  return {
    currentProcessingFile,
    setCurrentProcessingFile,
    numPages,
    pageNumber,
    scale,
    pdfInput,
    base64File,
    resizing,
    setResizing,
    moving,
    setMoving,
    isDrawing,
    setIsDrawing,
    isSelecting,
    setIsSelecting,
    rect,
    setRect,
    selectionRect,
    setSelectionRect,
    freeSelectStartPoint,
    setFreeSelectStartPoint,
    startPoint,
    setStartPoint,
    initialMousePosition,
    setInitialMousePosition,
    initialInputSize,
    setInitialInputSize,
    inputPosition,
    setInputPosition,
    currCursor,
    setCurrCursor,
    resizeDirection,
    setResizeDirection,
    selectToolActive,
    setSelectToolActive,
    typingToolActive,
    setTypingToolActive,
    expandToolBox,
    setExpandToolBox,
    focusedField,
    setFocusedField,
    highlightFields,
    setHighlightFields,
    textOverlayDisplayMode,
    setTextOverlayDisplayMode,
    unassignedFieldNames,
    activeEditorTool,
    pdfContainerRef,
    pdfScrollContainerRef,
    pdfDocumentRef,
    inputRef,
    childInputRef,
    fixedInputs,
    handleFieldChange,
    onDocumentLoadSuccess,
    onPageLoadSuccess,
    handleScaleChange,
    handlePageChange,
    handleToolToggle,
    handleEditorToolSelection,
    handleSave,
    handleClose,
    pushHistory,
    undo,
    redo,
    canRedo,
    canUndo,
    textSelectionMode,
    setTextSelectionMode,
    paisTableSelectActive,
    handleTogglePaisTableSelect,
  };
};
